import React, { useState, useEffect, useCallback } from "react";
import { SiHubspot } from "react-icons/si";
import { TbTarget, TbEye, TbEyeOff, TbLock, TbShieldCheck } from "react-icons/tb";
import { api } from "../../../lib/api";
import { Button, Field, Alert } from "../../../components";

// HubSpot — the second destination for the Attack Surface lead magnet.
//
// Shaped like GoHighLevelPanel on purpose: contact sync, then deal creation, in the order
// the two things happen when a lead unlocks a report. Two differences that matter:
//
//   * The token is never sent back to the browser. GET /hubspot/config answers with
//     has_token plus a masked hint, and this form posts SECRET_PLACEHOLDER back when the
//     admin did not retype it. (GoHighLevelPanel round-trips its token in plaintext; that
//     is the older convention, not one to copy.)
//   * There is no Location ID and no API version — a Private App token is scoped to one
//     portal and HubSpot versions its endpoints in the path.

const SECRET_PLACEHOLDER = "__unchanged__";
const DEFAULT_SOURCE = "Pipsqueak Lead Magnet";

const HubSpotPanel = ({ isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const [hasStoredToken, setHasStoredToken] = useState(false);
  const [tokenHint, setTokenHint] = useState("");
  const [tokenTouched, setTokenTouched] = useState(false);

  const [pipelines, setPipelines] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [pipelineError, setPipelineError] = useState("");

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [form, setForm] = useState({
    enabled: false,
    access_token: "",
    report_property_name: "",
    pipeline_id: "",
    pipeline_stage_id: "",
    source: DEFAULT_SOURCE,
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = useCallback(async () => {
    try {
      const d = await api.get("s2/api/hubspot/config");
      if (d?.configured) {
        setHasStoredToken(!!d.has_token);
        setTokenHint(d.token_hint || "");
        setTokenTouched(false);
        setForm({
          enabled: !!d.enabled,
          access_token: "",
          report_property_name: d.report_property_name || "",
          pipeline_id: d.pipeline_id || "",
          pipeline_stage_id: d.pipeline_stage_id || "",
          source: d.source || DEFAULT_SOURCE,
        });
      }
      setError("");
    } catch (e) {
      setError(e.message || "Could not load integration settings.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // What the server should use: the newly typed token, or the placeholder meaning
  // "the one you already have".
  const tokenForRequest = () =>
    tokenTouched && form.access_token ? form.access_token : SECRET_PLACEHOLDER;

  const ready = tokenTouched ? !!form.access_token : hasStoredToken;

  const fetchPipelines = async () => {
    if (!ready) { setPipelineError("Enter the access token first."); return; }
    setLoadingPipelines(true); setPipelineError("");
    try {
      const d = await api.post("s2/api/hubspot/pipelines", { access_token: tokenForRequest() });
      const list = d?.pipelines || [];
      setPipelines(list);
      if (list.length === 0) setPipelineError("No deal pipelines found in this portal.");
    } catch (e) {
      setPipelineError(e.message || "Could not fetch pipelines from HubSpot.");
    } finally { setLoadingPipelines(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      await api.post("s2/api/hubspot/test", { access_token: tokenForRequest() });
      setTestResult({ ok: true, message: "HubSpot accepted the token." });
    } catch (e) {
      setTestResult({ ok: false, message: e.message || "HubSpot rejected the token." });
    } finally { setTesting(false); }
  };

  const save = async () => {
    setBusy(true); setError(""); setSaved("");
    try {
      await api.post("s2/api/hubspot/integrate", {
        hubspot_access_token: tokenForRequest(),
        hubspot_report_property_name: form.report_property_name || null,
        hubspot_pipeline_id: form.pipeline_id || null,
        hubspot_pipeline_stage_id: form.pipeline_stage_id || null,
        hubspot_source: form.source,
        hubspot_enabled: form.enabled,
      });
      setSaved("HubSpot settings saved.");
      await load();
      setTimeout(() => setSaved(""), 3000);
    } catch (e) {
      setError(e.message || "Could not save. Check the access token.");
    } finally { setBusy(false); }
  };

  if (loading) return <p className="v2-hint">Loading…</p>;

  const stages = pipelines.find((p) => p.id === form.pipeline_id)?.stages || [];
  // A pipeline without a stage cannot create a deal, and the server refuses the save, so
  // say so here rather than letting the admin discover it on the round trip.
  const stageMissing = !!form.pipeline_id && !form.pipeline_stage_id;

  return (
    <div className="v2-settings-form v2-ghl">
      <Alert>{error}</Alert>
      {saved && <Alert tone="success">{saved}</Alert>}

      {/* Contact sync ---------------------------------------------------- */}
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--orange"><SiHubspot size={18} /></span>
          <div>
            <div className="v2-setting-name">HubSpot Integration</div>
            <div className="v2-setting-desc">Create or update a contact, and open a deal, when a lead unlocks their report</div>
          </div>
          <label className="v2-ghl-toggle" style={{ marginLeft: "auto" }}>
            <input type="checkbox" checked={form.enabled} disabled={!isAdmin}
                   aria-label="HubSpot integration active"
                   onChange={(e) => set({ enabled: e.target.checked })} />
            <span>{form.enabled ? "ACTIVE" : "INACTIVE"}</span>
          </label>
        </div>

        <div className={`v2-ghl-status${ready && form.enabled ? " on" : ""}`}>
          <span className="v2-ghl-dot" />
          {!form.enabled
            ? "Inactive — leads will not be sent to HubSpot"
            : ready
              ? "Ready to sync — contacts will be created or updated on lead capture"
              : "Add a Private App access token to start syncing"}
        </div>

        <Field label={<><TbLock size={13} /> Private App Access Token *</>}>
          <div className="v2-ghl-secret">
            <input className="v2-input" type={showToken ? "text" : "password"}
                   value={form.access_token} disabled={!isAdmin}
                   aria-label="Private app access token"
                   placeholder={hasStoredToken ? (tokenHint || "••••••••") : "pat-…"}
                   onChange={(e) => { setTokenTouched(true); set({ access_token: e.target.value }); }} />
            <button type="button" className="v2-ghl-reveal"
                    aria-label={showToken ? "Hide token" : "Show token"}
                    onClick={() => setShowToken((v) => !v)}>
              {showToken ? <TbEyeOff size={16} /> : <TbEye size={16} />}
            </button>
          </div>
          {hasStoredToken && !tokenTouched && (
            <p className="v2-ghl-help">
              {tokenHint === "unreadable"
                ? "A token is stored but cannot be read back — re-enter it to fix this."
                : "A token is already saved. Leave this blank to keep it."}
            </p>
          )}
          <p className="v2-ghl-help">
            <b>How to get it:</b> In HubSpot go to Settings → Integrations → Private Apps →
            Create a private app. Grant <code>crm.objects.contacts.write</code> and{" "}
            <code>crm.objects.deals.write</code>, then copy the access token.
          </p>
        </Field>

        <div>
          <Button onClick={test} disabled={testing || !ready}>
            {testing ? "Testing…" : "Test connection"}
          </Button>
          {testResult && (
            <Alert tone={testResult.ok ? "success" : "warning"}>{testResult.message}</Alert>
          )}
        </div>

        <Field label="Report Link Contact Property (Optional)">
          <input className="v2-input" value={form.report_property_name} disabled={!isAdmin}
                 aria-label="Report link contact property"
                 placeholder="scanned_domain_report"
                 onChange={(e) => set({ report_property_name: e.target.value })} />
          <div className="v2-ghl-help">
            <b>To save the report link on the contact:</b>
            <ol>
              <li>Go to <b>Settings → Properties → Create property</b></li>
              <li>Object type <b>Contact</b>, field type <b>Single-line text</b></li>
              <li>Save, then copy its <b>internal name</b> and paste it here</li>
            </ol>
            Leave blank to send no link. An internal name that does not exist in your portal
            makes HubSpot reject the whole contact, so this has no default.
          </div>
        </Field>
      </section>

      {/* Deal creation --------------------------------------------------- */}
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--violet"><TbTarget size={18} /></span>
          <div>
            <div className="v2-setting-name">Create Deals</div>
            <div className="v2-setting-desc">Open a deal in your HubSpot pipeline, associated with the new contact</div>
          </div>
        </div>

        <div className={`v2-ghl-status${form.pipeline_id && form.pipeline_stage_id ? " on" : ""}`}>
          <span className="v2-ghl-dot" />
          {form.pipeline_id && form.pipeline_stage_id
            ? "Pipeline sync active — a deal will be created for each lead"
            : "Pick a pipeline and stage to create deals alongside contacts"}
        </div>

        <div>
          <Button variant="primary" onClick={fetchPipelines}
                  disabled={loadingPipelines || !isAdmin || !ready}>
            {loadingPipelines ? "Fetching…" : "Fetch Pipelines & Stages"}
          </Button>
          {!ready && (
            <p className="v2-hint" style={{ marginTop: ".375rem" }}>
              The access token above is used to look these up.
            </p>
          )}
        </div>

        {pipelineError && <Alert tone="warning">{pipelineError}</Alert>}

        <div className="v2-grid2">
          <Field label="Pipeline">
            <select className="v2-input" value={form.pipeline_id} disabled={!isAdmin || pipelines.length === 0}
                    aria-label="Pipeline"
                    onChange={(e) => set({ pipeline_id: e.target.value, pipeline_stage_id: "" })}>
              <option value="">{pipelines.length ? "Select a Pipeline" : "Fetch pipelines first"}</option>
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Deal Stage"
                 error={stageMissing ? "Pick a stage — a pipeline without one cannot create a deal." : ""}>
            <select className="v2-input" value={form.pipeline_stage_id} aria-label="Deal stage"
                    disabled={!isAdmin || !form.pipeline_id}
                    onChange={(e) => set({ pipeline_stage_id: e.target.value })}>
              <option value="">{form.pipeline_id ? "Select a Stage" : "Select a pipeline first"}</option>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
        </div>

        {/* A saved pipeline from an earlier session has an id but no fetched list to name
            it from. Say so, rather than rendering an empty select that looks like the
            setting was lost. */}
        {form.pipeline_id && pipelines.length === 0 && (
          <p className="v2-hint">
            A pipeline is already saved. Fetch pipelines to see its name or change it.
          </p>
        )}
      </section>

      <div className="v2-ghl-note">
        <TbShieldCheck size={15} />
        <span>
          <b>How it works:</b> When a lead unlocks their report, their details are matched to a
          HubSpot contact by email — created if new, updated if they already exist. HubSpot runs
          alongside GoHighLevel; enabling one does not turn the other off.
        </span>
      </div>

      <div>
        <Button variant="primary" onClick={save} disabled={busy || !isAdmin || !ready || stageMissing}>
          {busy ? "Saving…" : "Save HubSpot settings"}
        </Button>
      </div>
    </div>
  );
};

export default HubSpotPanel;
